import { EMPTY_GUID } from 'constants/appConstants';
import dayjs from 'dayjs';
import { GcVariety, GUnit, MergedCrop } from 'interfaces/IGlobalCatalogue';
import { IInput, IUnit, IInputZone } from 'interfaces/IInput';

export const END_DATE_BEFORE_START_MESSAGE =
  'End date must be after start date';

// A zone that has not been populated yet. Built fresh each call so callers
// never share the `links` array reference.
const createEmptyZone = (): IInputZone => ({
  zoneName: '',
  zoneArea: NaN,
  areaUnit: null,
  geoData: null,
  links: [],
});

export const buildCropPatch = (option: MergedCrop): Partial<IInput> => ({
  crop: {
    cropName: option.name,
    globalId: option.globalId,
    variety: null,
  },
});

export const buildVarietyPatch = (
  draft: IInput | null,
  option: GcVariety | undefined
): Partial<IInput> => ({
  crop: {
    cropName: draft?.crop?.cropName ?? '',
    globalId: draft?.crop?.globalId ?? null,
    variety: option
      ? { varietyName: option.varietyName, globalId: option.varietyId }
      : null,
  },
});

export const buildZoneAreaPatch = (
  zone: IInputZone | null | undefined,
  value: number | null
): Partial<IInput> => ({
  // zoneArea is typed as a required number on IInputZone — NaN is used as the
  // "cleared, not yet valid" sentinel; isInputDraftValid guards on it.
  zone: {
    ...(zone ?? createEmptyZone()),
    zoneArea: value === null ? NaN : value,
  },
});

export const buildUnitPatch = (
  zone: IInputZone | null | undefined,
  option: GUnit
): Partial<IInput> => ({
  zone: {
    ...(zone ?? createEmptyZone()),
    areaUnit: { unit: option.code, globalId: option.globalId },
  },
});

// The Input schema documents startDate/endDate as "a local time set by the
// user", in explicit contrast to dateCreated/dateModified which are "the
// timestamp in UTC". So the day the user picked is stored with its wall-clock
// value preserved — serialised at UTC midnight — rather than converted to an
// instant, which would shift it to the previous day for every positive UTC
// offset. This matches the mobile app, whose picker does `new Date('YYYY-MM-DD')`.
//
// Only applied where the user actually picks a date. Dates already stored the
// old way are deliberately left alone: re-deriving a calendar day from an
// existing instant depends on the reader's timezone, so normalising on save
// could shift a date created in another timezone.
export const toCalendarDateIso = (date: Date | null): string | null => {
  if (!date) return null;
  const picked = dayjs(date);
  if (!picked.isValid()) return null;
  return `${picked.format('YYYY-MM-DD')}T00:00:00.000Z`;
};

// Mirrors the mobile PaddockPlanForm schema: the range is only checked when
// both dates are set, and an end date equal to the start date is allowed —
// only a strictly earlier end date is rejected.
//
// Compared at day granularity rather than by instant. The pickers write local
// midnight, but a startDate created on mobile can carry a real time of day; an
// instant comparison would then reject an end date on the same displayed day
// for anyone east of UTC, with both fields visibly reading the same date.
export const isEndDateBeforeStartDate = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): boolean => {
  if (!startDate || !endDate) return false;
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return false;
  return end.startOf('day').isBefore(start.startOf('day'));
};

// The required-field rule for an Input document: a crop, a start date, an area
// and a start/end range that is the right way round. Shared by the workspace
// autosave gate and the create-plan modal so a new plan and an edited plan can
// never diverge on what "valid" means.
export const isInputDraftValid = (draft: IInput | null): boolean => {
  if (!draft) return false;
  const hasCrop = Boolean(draft.crop?.globalId || draft.crop?.cropName);
  const hasStartDate = Boolean(draft.startDate);
  const hasArea = draft.zone ? !Number.isNaN(draft.zone.zoneArea) : false;
  const hasValidDateRange = !isEndDateBeforeStartDate(
    draft.startDate,
    draft.endDate
  );
  return hasCrop && hasStartDate && hasArea && hasValidDateRange;
};

// A globalId is only meaningful when it points at a real record. Zone and Farm
// documents can carry either an absent value or the empty GUID placeholder,
// and both mean "not set" — the repo already treats EMPTY_GUID that way in the
// Global Catalogue grids.
const hasResolvedGlobalId = (globalId: string | null | undefined): boolean =>
  Boolean(globalId) && globalId !== EMPTY_GUID;

// Paddocks can carry an area unit with no usable globalId. The unit dropdown
// still renders correctly (resolveUnitValue falls back to the code), but the
// value persisted on the document would keep the unusable globalId — so fill
// it in from the GUnit catalogue on every write. An already-resolved unit is
// left alone, and an unmatched code is returned untouched rather than blanked.
export const resolveAreaUnit = (
  areaUnit: IUnit | null | undefined,
  gUnits: GUnit[]
): IUnit | null => {
  if (!areaUnit) return null;
  if (hasResolvedGlobalId(areaUnit.globalId)) return areaUnit;
  const matchedUnit = gUnits.find((unit) => unit.code === areaUnit.unit);
  return matchedUnit
    ? { unit: matchedUnit.code, globalId: matchedUnit.globalId }
    : areaUnit;
};

// Maps the zone's stored area unit back onto a GUnit globalId for the unit
// dropdown, matching on globalId first and falling back to the unit code.
export const resolveUnitValue = (
  zone: IInputZone | null | undefined,
  gUnits: GUnit[]
): string | undefined => {
  const areaUnit = zone?.areaUnit;
  if (!areaUnit) return undefined;
  const matchedUnit = gUnits.find(
    (unit) => unit.globalId === areaUnit.globalId || unit.code === areaUnit.unit
  );
  return matchedUnit?.globalId;
};
